import moment from 'moment';

const CONFIRMED_LIST_ROLES = [
    { role: 'supervisor', confirmedList: 'confirmedSupervisorList', legacyList: 'supervisorList' },
    { role: 'lead', confirmedList: 'confirmedLeadList', legacyList: 'leadList' },
    { role: 'tech', confirmedList: 'confirmedTechList', legacyList: null },
];

// Statuses that mean the user is no longer booked on the project.
// startsWith also covers the "denied - unseen" and "dropped off - unseen" variants.
const NOT_BOOKED_STATUSES = ['removed', 'denied', 'dropped off'];

const isBookedStatus = (status) => {
    if (typeof status !== 'string') return false;
    return !NOT_BOOKED_STATUSES.some((notBooked) => status.startsWith(notBooked));
};

const projectDayRange = (project) => {
    const start = moment(project.startTime, 'YYYY-MM-DD hh:mm').startOf('day');
    var end = project.endTime ? moment(project.endTime, 'YYYY-MM-DD hh:mm').startOf('day') : start;
    if (!end.isValid() || end.isBefore(start)) {
        end = start;
    }
    return { start, end };
};

// Returns { role, status } if the user is booked on the project, otherwise null.
// Checks all three confirmed lists, falling back to the legacy array lists
// (supervisorList/leadList) when the confirmed list doesn't exist yet.
const findUserBooking = (project, userID) => {
    for (var i = 0; i < CONFIRMED_LIST_ROLES.length; i++) {
        const { role, confirmedList, legacyList } = CONFIRMED_LIST_ROLES[i];

        if (project[confirmedList]) {
            const status = project[confirmedList][userID];
            if (status && isBookedStatus(status)) {
                return { role, status };
            }
        } else if (legacyList && Array.isArray(project[legacyList]) && project[legacyList].includes(userID)) {
            return { role, status: 'confirmed' };
        }
    }
    return null;
};

// Scans the given projects map for active projects (other than the current one)
// that overlap the current project's day(s) and already have the user booked.
// Returns [{ projectID, project, role, status }] sorted by start time.
export const findSameDayBookings = (projects, currentProject, userID) => {
    const conflicts = [];
    if (!projects || !currentProject || !userID) return conflicts;

    const currentID = currentProject.refLocation ? currentProject.refLocation.id : null;
    const currentRange = projectDayRange(currentProject);
    if (!currentRange.start.isValid()) return conflicts;

    Object.entries(projects).forEach(([projectID, project]) => {
        if (!project || projectID === currentID) return;
        if (project.phase === 'deleted' || project.phase === 'finished') return;

        const range = projectDayRange(project);
        if (!range.start.isValid()) return;

        const sameDay = range.start.isSameOrBefore(currentRange.end, 'day') &&
                        currentRange.start.isSameOrBefore(range.end, 'day');
        if (!sameDay) return;

        const booking = findUserBooking(project, userID);
        if (booking) {
            conflicts.push({ projectID, project, role: booking.role, status: booking.status });
        }
    });

    conflicts.sort((a, b) => {
        const aStart = moment(a.project.startTime, 'YYYY-MM-DD hh:mm');
        const bStart = moment(b.project.startTime, 'YYYY-MM-DD hh:mm');
        return aStart.valueOf() - bStart.valueOf();
    });

    return conflicts;
};
